import { useEffect, useRef, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useVault } from "@/context/VaultContext";
import { creatorText, frameFromVideo, timecode } from "@/lib/watermark";
import { saveFrame } from "@/lib/store";
import { stamp, tzAbbr } from "@/lib/util";
import { useObjectUrl } from "./Attachments";

function Overlay({ profile, assets, t, date, note }) {
  const text = creatorText(profile);
  return (
    <div className="pointer-events-none absolute inset-0" data-testid="video-watermark-overlay">
      {text && <div className="absolute inset-0 flex items-center justify-center" style={{ opacity: profile.opacity }}>
        <span className="font-heading font-bold text-white text-3xl sm:text-5xl -rotate-[22deg] drop-shadow-[0_0_2px_rgba(0,0,0,0.9)]">{text}</span>
      </div>}
      <div className="absolute right-3 bottom-16 flex flex-col items-end gap-1" style={{ opacity: profile.opacity }}>
        {profile.stamp.logo && assets.logo && <img src={profile.logo} alt="" className="h-10" />}
        {profile.stamp.signature && assets.sig && <img src={profile.signature} alt="" className="h-10 drop-shadow-[0_0_3px_white]" />}
      </div>
      <div className="absolute top-0 inset-x-0 bg-black/70 text-white px-3 py-1.5 flex justify-between text-[11px] font-mono">
        <span>TC {timecode(t)} • {stamp(new Date(date))} {tzAbbr(new Date(date))}</span>
        <span className="hidden sm:inline">★ StarVault • by Sammy Star{note.ref ? ` • ${note.ref}` : ""}</span>
      </div>
    </div>
  );
}

export function VideoDialog({ file, note, onClose, onSaved }) {
  const { profile, assets } = useVault();
  const url = useObjectUrl(file.blob);
  const ref = useRef(null);
  const [wm, setWm] = useState(true);
  const [t, setT] = useState(0);
  useEffect(() => { const v = ref.current; if (!v) return; const h = () => setT(v.currentTime); v.addEventListener("timeupdate", h); return () => v.removeEventListener("timeupdate", h); }, [url]);
  const snap = async () => {
    const v = ref.current;
    if (!v?.videoWidth) return toast.error("Video frame not ready yet");
    const blob = await frameFromVideo(v, { profile, assets, date: file.createdAt, title: note.title, ref: note.ref });
    await saveFrame(file, blob, timecode(v.currentTime));
    onSaved();
    toast.success(`Watermarked frame saved at ${timecode(v.currentTime)}`);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="video-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">{file.name}</DialogTitle>
          <DialogDescription>Raw original — unaltered. Overlay is display-only.</DialogDescription>
        </DialogHeader>
        <div className="relative bg-black rounded-md overflow-hidden">
          <video ref={ref} src={url || undefined} controls playsInline className="w-full max-h-[60vh]" data-testid="video-player" />
          {wm && <Overlay profile={profile} assets={assets} t={t} date={file.createdAt} note={note} />}
        </div>
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-2">
            <Switch id="wm" checked={wm} onCheckedChange={setWm} data-testid="video-watermark-toggle" />
            <Label htmlFor="wm">Watermark overlay</Label>
          </div>
          <Button onClick={snap} data-testid="snap-frame-button"><Camera className="h-4 w-4 mr-1" />Snap Watermarked Frame</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
